import { interval as observableInterval, Observable, Subscription } from 'rxjs';
import { Injectable, isDevMode } from '@angular/core';

import { StoreService } from '../_service/store.service';
import { HelperService } from '../_service/helper.service';

import { Histogram } from '../_helper/histogram';
import { Speed } from '../_helper/speed';
import { share } from 'rxjs/operators';

@Injectable()
export class DownloadService {

    progress: number;
    progress$: Observable<any>;
    progressObserver: any;
    histogram$: Observable<any>;
    histogramObserver: any;
    all: number;
    total: number;
    startTime: Date;
    currentTime: Date;
    duration: number;
    downloadedData = 0;

    progressArray: any[];

    threads = 4;
    threadCount = 0;
    threadCounter: number = this.threads - 1; // all - 1
    private isCancelled: boolean = false;

    testDuration = 20000; // in ms
    // testDuration: number = 1000;
    isDurationPassed: Boolean = false;
    requestArray: any[] = [];

    notify$: Observable<any>;
    notifyObserver: any;

    subSpeed: Subscription;
    subHistogram: Subscription;

    downloadList: number[] = [];
    timeList: number[] = [];

    private histo: Histogram = new Histogram();
    private speed: Speed = new Speed();

    constructor(private storeService: StoreService,
                private helperService: HelperService) {
        if (isDevMode()) {
            // this.testDuration = 1500;
            this.testDuration = 3000;
        }
        this.progress$ = new Observable((observer) => {
            this.progressObserver = observer;
        }).pipe(share());
        this.notify$ = new Observable((observer) => {
            this.notifyObserver = observer;
        }).pipe(share());
        this.histogram$ = new Observable((observer) => {
            this.histogramObserver = observer;
        });
        if (this.helperService.isMobileOperatingSystem()) {
            this.threads = 2;
        } else {
            this.threads = 4;
        }
    }

    download(): Observable<any> {
        // Clean up any existing subscriptions first
        if (this.subSpeed) {
            this.subSpeed.unsubscribe();
        }
        if (this.subHistogram) {
            this.subHistogram.unsubscribe();
        }

        this.notifyObserver.next('start');

        this.requestArray = [];
        this.isCancelled = false;
        this.isDurationPassed = false;
        this.threadCount = 0;
        this.threadCounter = this.threads - 1; // Reset thread counter
        this.all = 0;
        this.total = 0;
        this.progressArray = [];
        this.startTime = new Date();
        this.downloadList = [];
        this.timeList = [];

        this.speed = new Speed();
        this.histo = new Histogram();

        this.subSpeed = observableInterval(100).subscribe((val) => {
            this.calculateSpeed();
        });

        this.subHistogram = observableInterval(250).subscribe((val) => {
            this.calculateHistogram();
        });

        return new Observable((observer) => {
            setTimeout(() => {
                this.cancelAll(this, observer);
            }, this.testDuration);

            for (let i = 0; i < this.threads; ++i) {
                this.downloadThreat(observer, i);
            }
        });
    }

    downloadThreat(observer: any, i: number) {
        // Don't create new requests if cancelled
        if (this.isCancelled) {
            return;
        }

        const url: string = this.storeService.testServer.downloadTestUrl;
        let xhr: XMLHttpRequest = new XMLHttpRequest();
        xhr.responseType = 'blob';

        xhr.onreadystatechange = () => {
            if (this.isCancelled) {
                xhr.onreadystatechange = null;
                xhr.onprogress = null;
                xhr.onload = null;
                return; // Ignore any responses after cancellation
            }
            if (xhr.readyState === 2) { // HEADERS_RECEIVED
                const contentLength = xhr.getResponseHeader('Content-Length');
                if (contentLength) {
                    this.total += parseInt(contentLength);
                }
            } else if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    xhr = null;
                    // Don't complete the download test here - let the timer handle it
                } else {
                    // Handle error case but don't complete here either
                    console.log('Download request failed:', xhr.status);
                }
            }
        };

        xhr.onload = evt => {
            if (this.isCancelled) {
                return; // Don't start new threads after cancellation
            }
            this.threadCounter++;
            if (!this.isDurationPassed) {
                this.downloadThreat(observer, this.threadCounter);
            }
        };

        xhr.onabort = () => {
            // console.log("xhr.onabort");
        };

        xhr.onprogress = (event) => {
            if (this.isCancelled) {
                return; // Ignore progress updates after cancellation
            }
            this.createProgressHandler(new Date(), i)(event);
        };

        xhr.open('GET', url + '&r=' + Math.random(), true);
        xhr.send();

        this.requestArray.push(xhr);
    }

    calculateSpeed() {
        this.sumUpData();
        this.downloadList.push(this.downloadedData);
        this.timeList.push((new Date()).getTime());

        const speed = this.speed.calculateCurrentSpeedMbps(this.downloadList, this.timeList, 100);
        const progress: number = ((new Date()).getTime() - this.startTime.getTime()) / this.testDuration;

        const object = {
            speed: speed,
            progress: progress,
        };

        this.progressObserver.next(object);
    }

    sumUpData() {
        let sum = 0;
        for (let i = this.progressArray.length - 1; i >= 0; i--) {
            const obj = this.progressArray[i];
            if (obj != null && obj.bytes != undefined) {
                sum += obj.bytes;
            }
        }
        this.downloadedData = sum;
        this.storeService.test.downloadedData = sum;
    }

    createProgressHandler(start: any, i: any) {
        const self = this;
        return function (event) {
            if (self.progressArray[i] == null || self.progressArray[i] == undefined) {
                self.progressArray[i] = {bytes: 0};
            }

            const bytes = event.loaded;
            self.progressArray[i].bytes = bytes;
        };
    }

    cancelAll(self, observer) {
        self.isCancelled = true;  // Set flag before aborting
        self.isDurationPassed = true;

        // Clear all progress data immediately
        self.progressArray = [];

        // Abort all requests and clear their handlers
        for (let i = 0; i < self.requestArray.length; ++i) {
            const xhr: XMLHttpRequest = self.requestArray[i];
            try {
                // Clear all event handlers first
                xhr.onreadystatechange = null;
                xhr.onprogress = null;
                xhr.onload = null;
                xhr.onabort = null;
                xhr.onerror = null;
                // Then abort
                xhr.abort();
            } catch (e) {
                console.log('Error aborting XHR:', e);
            }
        }
        // Clear the request array
        self.requestArray = []

        // Clean up subscriptions
        if (this.subSpeed) {
            try {
                this.subSpeed.unsubscribe();
            } catch (e) {
                console.log('Error unsubscribing speed:', e);
            }
        }
        if (this.subHistogram) {
            try {
                this.subHistogram.unsubscribe();
            } catch (e) {
                console.log('Error unsubscribing histogram:', e);
            }
        }

        // Force completion immediately and with backup timer
        try {
            self.notifyObserver.next('end');
            observer.complete();
        } catch (e) {
            console.log('Error completing observer:', e);
        }

        // Backup completion in case the immediate one fails
        setTimeout(() => {
            try {
                if (!observer.closed) {
                    observer.complete();
                }
            } catch (e) {
                // Silent fail - observer already completed
            }
        }, 50);
    }

    calculateHistogram() {
        const progressTime: number = ((new Date()).getTime() - this.startTime.getTime()) / this.testDuration;
        const gram = this.histo.createHistogramValues(this.downloadList, this.timeList, 0, progressTime, this.testDuration, 60);
        this.storeService.test.downloadHistogram = gram;
        this.histogramObserver.next(gram);
    }

}
